import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject, Input, Output, EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionList } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { catchError, firstValueFrom, of, Subscription, switchMap, takeWhile, tap, timer } from 'rxjs';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import {
  ICompaniesShortList,
  IParkerActiveListForInvoiceGeneration,
  IPrelimInvoiceResponse,
  IGBPrelimPastRequest,
  IInvoiceSystemGenerationDefaults,
} from 'src/app/shared/interfaces';
import { spaceArCoreHttpApi } from 'src/app/shared/code/libraries/http-data/spaceArHttpApi';

@Component({
  selector: 'app-multi-month-past',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatListModule,
    MatCheckboxModule,
  ],
  templateUrl: './multi-month-past.component.html',
  styleUrl: './multi-month-past.component.scss',
})
export class MultiMonthPastComponent implements OnInit {
  companyList: ICompaniesShortList[] = [];
  parkerList: IParkerActiveListForInvoiceGeneration[] = [];
  selectedMonths?: number = 1;
  pastMonthDateOptions: Date[] = [];
  invoiceLineDescription: string = '';
  isInvoiceLineDescriptionCustomizationEnabled: boolean = false;
  selectedCompanyIds: string[] = [];
  currentSelectedCompanyId: string = '';
  isSelectAllParkersChecked: boolean = false;
  isParkerSelectionReadOnly: boolean = false;
  isProcessing: boolean = false;
  private hasInitialized: boolean = false;
  private isCompanyMultiSelectModifierHeld: boolean = false;
  private lastCancelGenerationToken: number = 0;
  private generationRequestSubscription: Subscription | null = null;
  private pollingSubscription: Subscription | null = null;
  private currentJobId: string | null = null;
  private readonly POLL_INTERVAL_MS = 2000;
  disablePrelimGeneration: boolean = false;
  revenueDate: Date = new Date();
  prelimList: IPrelimInvoiceResponse[] = [];
  invoiceDate: Date = new Date();
  dueDate: Date = new Date();
  numberOfMonths: number = 0;
  companyId: string = '';
  numberOfDaysToAddForDueDate: number = 0;
  invoiceSystemDefaults: IInvoiceSystemGenerationDefaults = undefined as any;
  endMonth: Date = new Date();
  companyMultiSelectModifierActive: boolean = false;

  get isMultipleCompaniesSelected(): boolean {
    return this.selectedCompanyIds.length > 1;
  }

  @Input()
  set systemDefaults(defaults: IInvoiceSystemGenerationDefaults) {
    this.invoiceSystemDefaults = defaults;
    const netDays = defaults?.gbMultiPastMonthDueDateNetDays;
    const numberOfMOnths = defaults?.pastMonthInvoiceLimit;
    const today = new Date();
    this.dueDate =
      netDays !== undefined
        ? new Date(today.getFullYear(), today.getMonth(), today.getDate() + netDays)
        : new Date(today.getFullYear(), today.getMonth(), today.getDate());

    this.endMonth = new Date(this.dueDate.getFullYear(), this.dueDate.getMonth(), 1);

    if (numberOfMOnths !== undefined) {
      this.numberOfMonths = numberOfMOnths;
      this.setPastMonthDateOptions();
    }
  }

  @Input()
  set resetPrelimButton(_: number) {
    this.disablePrelimGeneration = false;
  }

  @Input()
  set invoiceLineCustomizationStatus(status: boolean) {
    this.isInvoiceLineDescriptionCustomizationEnabled = status;
  }

  @Input()
  set companyListData(companies: ICompaniesShortList[]) {
    this.companyList = companies ?? [];
  }

  @Input()
  set lotNumberResetToken(_: number) {
    if (!this.hasInitialized) {
      return;
    }
    this.resetForLotChange();
  }

  @Input()
  set cancelGenerationToken(token: number) {
    if (!this.hasInitialized || token === this.lastCancelGenerationToken) {
      return;
    }

    this.lastCancelGenerationToken = token;
    this.cancelGeneration();
  }

  @Output() prelimInvoiceListChange = new EventEmitter<IPrelimInvoiceResponse[]>();
  @Output() processingStateChange = new EventEmitter<boolean>();
  @Output() notifyOfErrorDuringInvoiceGeneration = new EventEmitter<string>();

  @ViewChild('parkerSelectionList') parkerSelectionList?: MatSelectionList;

  dataService = inject(DataService);
  appGeneralStateService = inject(AppGeneralStateService);
  companyHttpService = inject(spaceArCoreHttpApi);

  ngOnInit(): void {
    // this.setPastMonthDateOptions();
    this.hasInitialized = true;
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private setPastMonthDateOptions(): void {
    const baseDate = this.dueDate ?? new Date();
    this.pastMonthDateOptions = Array.from({ length: this.numberOfMonths }, (_, index) => {
      return new Date(baseDate.getFullYear(), baseDate.getMonth() - (index + 1), 1);
    });

    if (this.pastMonthDateOptions.length > 0) {
      this.selectedMonths = 1;
      this.invoiceDate = this.pastMonthDateOptions[0];
    }
  }

  onSetDates(): void {
    this.numberOfDaysToAddForDueDate = this.invoiceSystemDefaults?.gbMultiPastMonthDueDateNetDays ?? 0;
    const currentDate = new Date();
    this.dueDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() + this.numberOfDaysToAddForDueDate,
    );
    this.endMonth = new Date(this.dueDate.getFullYear(), this.dueDate.getMonth(), 1);

    this.revenueDate = new Date(this.dueDate);
  }

  onEndMonthSelectionChange(): void {
    const selectedIndex = this.selectedMonths;
    if (selectedIndex === undefined) {
      return;
    }

    if (selectedIndex > 0 && selectedIndex <= this.pastMonthDateOptions.length) {
      this.invoiceDate = this.pastMonthDateOptions[selectedIndex - 1];
    }
  }

  async getParkerList(companyId: string): Promise<void> {
    const parkerListTest = await firstValueFrom(
      this.companyHttpService.getActivePastParkerListByCompanyId(companyId, this.invoiceDate, this.dueDate),
    );
    this.parkerList = parkerListTest;
  }

  async onCompanySelectionChange(companyIds: string[]): Promise<void> {
    const previousSelection = this.selectedCompanyIds;
    const previousCompanyId = this.currentSelectedCompanyId;

    this.selectedCompanyIds = this.resolveCompanySelection(previousSelection, companyIds);
    this.companyMultiSelectModifierActive = this.isMultipleCompaniesSelected;
    // The first company picked stays the parker anchor until it is deselected.
    this.currentSelectedCompanyId = this.selectedCompanyIds.includes(previousCompanyId)
      ? previousCompanyId
      : (this.selectedCompanyIds[0] ?? '');
    this.companyId = this.currentSelectedCompanyId;

    await this.applyCompanySelection(this.currentSelectedCompanyId !== previousCompanyId);
  }

  onCompanyListPointerDown(event: MouseEvent): void {
    this.isCompanyMultiSelectModifierHeld = event.ctrlKey || event.metaKey;
  }

  onCompanyListKeydown(event: KeyboardEvent, companySelectionList: MatSelectionList): void {
    this.isCompanyMultiSelectModifierHeld = event.ctrlKey || event.metaKey;

    // MatSelectionList only wires select-all to Ctrl+A, so mirror it for the macOS Command key.
    if (event.metaKey && !event.ctrlKey && event.key.toLowerCase() === 'a') {
      event.preventDefault();

      if (companySelectionList.options.some((option) => !option.selected)) {
        companySelectionList.selectAll();
      } else {
        companySelectionList.deselectAll();
      }
    }
  }

  // A plain click replaces the selection; Ctrl/Cmd clicks and select-all keep the list's own toggle result.
  private resolveCompanySelection(previousSelection: string[], companyIds: string[]): string[] {
    if (this.isCompanyMultiSelectModifierHeld) {
      return companyIds;
    }

    const added = companyIds.filter((companyId) => !previousSelection.includes(companyId));
    const removed = previousSelection.filter((companyId) => !companyIds.includes(companyId));

    if (added.length + removed.length !== 1) {
      return companyIds;
    }

    return [added[0] ?? removed[0]];
  }

  async onStartMonthChange(): Promise<void> {
    this.onEndMonthSelectionChange();
    await this.applyCompanySelection(true);
  }

  private async applyCompanySelection(shouldReloadParkers: boolean): Promise<void> {
    if (!this.currentSelectedCompanyId) {
      this.parkerList = [];
      this.parkerSelectionList?.deselectAll();
      this.isSelectAllParkersChecked = false;
      this.isParkerSelectionReadOnly = false;
      return;
    }

    if (shouldReloadParkers) {
      await this.getParkerList(this.currentSelectedCompanyId);
    }

    // With multiple companies the select-all toggle is not offered, but every parker is still included.
    this.isSelectAllParkersChecked = !this.isMultipleCompaniesSelected;

    if (this.parkerList.length === 0) {
      this.isParkerSelectionReadOnly = false;
      return;
    }

    setTimeout(() => {
      this.parkerSelectionList?.selectAll();
      this.isParkerSelectionReadOnly = true;
    });
  }

  onToggleSelectAllParkers(parkerSelectionList: MatSelectionList): void {
    if (this.isSelectAllParkersChecked) {
      parkerSelectionList.selectAll();
      this.isParkerSelectionReadOnly = true;
      return;
    }

    parkerSelectionList.deselectAll();
    this.isParkerSelectionReadOnly = false;
  }

  private resetForLotChange(): void {
    this.parkerList = [];
    this.selectedCompanyIds = [];
    this.companyMultiSelectModifierActive = false;
    this.currentSelectedCompanyId = '';
    this.companyId = '';
    this.parkerSelectionList?.deselectAll();
    this.selectedMonths = 1;
    this.invoiceLineDescription = '';
    this.isSelectAllParkersChecked = false;
    this.isParkerSelectionReadOnly = false;
    this.disablePrelimGeneration = false;

    this.onSetDates();
    this.setPastMonthDateOptions();
  }

  onGeneratePreliminaryInvoice(): void {
    const invoiceCriteria: IGBPrelimPastRequest = this.setInvoiceSearchCriteria();

    this.isProcessing = true;
    this.processingStateChange.emit(true);
    this.stopPolling();

    this.generationRequestSubscription?.unsubscribe();
    this.generationRequestSubscription = this.dataService
      .gb_GeneratePrelimInvoiceForPastMonths(invoiceCriteria)
      .subscribe({
        next: (response) => {
          this.currentJobId = response.jobId;
          this.startPolling(response.jobId);
        },
        error: (error) => {
          this.notifyOfErrorDuringInvoiceGeneration.emit(
            this.getGenerationSubmissionErrorMessage(error),
          );
          this.isProcessing = false;
          this.processingStateChange.emit(false);
          this.generationRequestSubscription = null;
        },
        complete: () => (this.generationRequestSubscription = null),
      });
  }

  private getGenerationSubmissionErrorMessage(error: any): string {
    if (error?.status === 409 && typeof error.error?.message === 'string') {
      return error.error.message;
    }

    return '';
  }

  private startPolling(jobId: string): void {
    this.stopPolling();
    this.pollingSubscription = timer(0, this.POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.dataService.GetInvoiceGenerationJobStatus(jobId)),
        tap((status) => this.processingStateChange.emit(status.status === 'Queued' || status.status === 'Processing')),
        takeWhile((status) => status.status === 'Queued' || status.status === 'Processing', true),
        catchError(() => {
          this.notifyOfErrorDuringInvoiceGeneration.emit(
            'Error checking job status. The job may still be processing. Please refresh and check the preliminary invoices list.',
          );
          this.isProcessing = false;
          this.processingStateChange.emit(false);
          this.currentJobId = null;
          return of(null);
        }),
      )
      .subscribe((status) => {
        if (!status) return;
        if (status.status === 'Completed') {
          this.prelimList = status.result ?? [];
          this.updateDueDateOnPrelimList();
          this.disablePrelimGeneration = this.prelimList.length > 0;
          this.currentJobId = null;
        } else if (status.status === 'Failed') {
          this.notifyOfErrorDuringInvoiceGeneration.emit(status.errorMessage ?? 'Past-month invoice generation failed.');
          this.isProcessing = false;
          this.processingStateChange.emit(false);
          this.currentJobId = null;
        } else if (status.status === 'Cancelled') {
          this.isProcessing = false;
          this.processingStateChange.emit(false);
          this.currentJobId = null;
        }
      });
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  cancelGeneration(): void {
    if (!this.isProcessing && !this.generationRequestSubscription) {
      return;
    }

    this.generationRequestSubscription?.unsubscribe();
    this.stopPolling();
    this.generationRequestSubscription = null;
    this.currentJobId = null;
    this.isProcessing = false;
    this.processingStateChange.emit(false);
  }

  updateDueDateOnPrelimList(): void {
    this.prelimList.forEach((row) => {
      row.dueDate = new Date(this.dueDate);
    });
    this.processingStateChange.emit(false);
    this.prelimInvoiceListChange.emit(this.prelimList);
  }

  setInvoiceSearchCriteria(): IGBPrelimPastRequest {
    let invoiceCriteria: IGBPrelimPastRequest = {} as IGBPrelimPastRequest;

    invoiceCriteria.lotNumber = this.appGeneralStateService.currentLotNumber;
    invoiceCriteria.entityIdList = this.selectedCompanyIds.join(',');
    invoiceCriteria.invoiceDate = this.invoiceDate;
    invoiceCriteria.dueDate = this.dueDate;
    invoiceCriteria.parkerIdList = this.getParkerIdList();
    invoiceCriteria.userName = this.appGeneralStateService.userName;
    invoiceCriteria.gbLineDescription = this.invoiceLineDescription;

    // invoiceCriteria.lotNumber = this.appGeneralStateService.currentLotNumber;
    // invoiceCriteria.entityIdList = '11152022230423481604';
    // invoiceCriteria.invoiceDate = new Date(2026, 0, 1);
    // invoiceCriteria.dueDate = new Date(2026, 3, 27);
    // invoiceCriteria.parkerIdList = this.getParkerIdList();
    // invoiceCriteria.userName = this.appGeneralStateService.userName;
    // invoiceCriteria.gbLineDescription = this.invoiceLineDescription;

    return invoiceCriteria;
  }

  // getParkerIdList(): string {
  //   const selected = this.parkerSelectionList?.selectedOptions.selected ?? [];
  //   if (selected.length === this.parkerList.length) {
  //     return '';
  //   }
  //   return selected.map((option) => option.value).join(',');
  // }

  // This is a placeholder function to construct the company ID list string based on selected companies.
  // This implementation only will pass one company Id.
  getCompanyIdList(): string {
    let companyIdList: string = '';

    companyIdList = this.companyId;

    return companyIdList;
  }

  // Gets the list of Parkers that are active and should be included in the invoice generation
  getParkerIdList(): string {
    // 20260509: Ken Moss
    // Changed to ALWAYS pass all ParkerPayType = 'P' even if all items are selected.
    const selectedIds = new Set<string>(
      (this.parkerSelectionList?.selectedOptions.selected ?? []).map((option) => option.value),
    );

    return this.parkerList
      .filter((parker) => selectedIds.has(parker.parkerId) && (parker.parkerPayType ?? '').toUpperCase() === 'P')
      .map((parker) => parker.parkerId)
      .join(',');
  }
}
